export const FILTER_FUNCTION_CONCEPT_DOC = `## What is a filter function?

A **collection** shows a list of elements of one type — for example a collection of KPIs. Which elements appear in it is decided by two things:

1. **Filter conditions** — simple checks you write yourself in this panel, like *"only elements whose \`role\` property is \`source\`"*.
2. **A filter function** — a smarter check built into the platform, for situations conditions can't express.

You can use either, or both. When you use both, an element must pass the filter conditions **and** the filter function to appear in the collection. If you set neither, the collection shows every element of its type in the project.

## When do I need one?

Use a filter function when the decision depends on **where the user is** or on something more involved than comparing a property to a value. The most common case: collections inside a section where users organize elements into their own folders — a plain condition can't know which folder the user has open, but the *In current folder* function can.

If a simple comparison is all you need ("status is \`active\`", "role is one of \`source, derived\`"), prefer filter conditions — they are easier to read and the platform can check them more thoroughly.

## Which functions exist?

The list below is always current — it comes from the same catalog the dropdown uses. Each function says what it does and what it needs in order to work. If a function has a **requirement** and this part of your structure doesn't meet it, the collection will simply show nothing — so read the requirement note before picking one.

## Can I get a new one?

Yes — filter functions are added by the platform team. They are small, fast checks; a function can look at the element itself, at where the user currently is in the navigation, and at the "parent" element the collection belongs to (see Parent bindings). It cannot load extra data or call other systems.

Good things to ask for, as inspiration:

- *"Show only elements linked to the element the user has open"* — e.g. only datasets connected to the current source system.
- *"Show only elements in a certain state"* when the state is derived from several properties at once.
- *"Show only elements the current user owns."*

When requesting one, describe **which collection** it is for and **the rule in plain words** — the platform team handles the rest.`;
export const FILTER_FUNCTION_DEFINITIONS = [
    {
        key: "projectTreeFolder.inCurrentFolder",
        displayName: "In current folder",
        description: "Shows only the elements placed in the folder the user is currently viewing.",
        documentation: `In some sections of the project tree, users can create their own folders
(the **+** button on a folder row, then typing a name where it says
"New folder") and move elements into them.

When a collection uses **In current folder**, it stops showing every element
of its type and instead shows only the ones placed in the folder the user
has open. Each folder then shows its own elements — which is exactly what
users expect from folders.

**Example.** In the Solution section, users organize source systems into
folders they create themselves. The "Source systems" collection uses
*In current folder*: opening the folder "CRM data" shows only the source
systems placed there, not every source system in the project.

**When to use it:** on any collection that lives inside a section with user
folders turned on.

**When not to use it:** anywhere else. If user folders are not enabled above
this collection, there is no "current folder", and the collection will
always be empty.

**Good to know:** a new element created from this collection lands in the
folder the user is viewing, so it appears immediately — the "New …" buttons
keep working as expected.`,
        requirements: `Only works inside a section where **user folders** are turned on (Display
settings → user folders on a folder above this collection). Anywhere
else, the collection will always be empty.`,
        scope: { kind: "anyCollection" },
        createSafe: true,
        fn: ({ entity, pathScope }) => entity.projectTreeFolderId === pathScope.projectTreeFolderId,
    },
];
const FILTER_FUNCTIONS = Object.fromEntries(FILTER_FUNCTION_DEFINITIONS.map((definition) => [definition.key, definition.fn]));
const CREATE_SAFE_FILTER_FUNCTION_KEYS = new Set(FILTER_FUNCTION_DEFINITIONS.filter((definition) => definition.createSafe).map((definition) => definition.key));
export function listFilterFunctionKeys() {
    return Object.keys(FILTER_FUNCTIONS);
}
export function listFilterFunctionDefinitions() {
    return FILTER_FUNCTION_DEFINITIONS;
}
export function getFilterFunctionDefinition(key) {
    return FILTER_FUNCTION_DEFINITIONS.find((definition) => definition.key === key) ?? null;
}
export function hasFilterFunctionKey(key) {
    return typeof key === "string" && key.length > 0 && key in FILTER_FUNCTIONS;
}
export function getFilterFunction(key) {
    if (!hasFilterFunctionKey(key))
        return null;
    return FILTER_FUNCTIONS[key] ?? null;
}
export function isCreateSafeFilterFunctionKey(key) {
    return typeof key === "string" && CREATE_SAFE_FILTER_FUNCTION_KEYS.has(key);
}
export function validateFilterFnKeyChoice(value, nodeKind, entityTypeKey) {
    if (value === undefined || value === null || value === "") {
        return { key: null, issues: [] };
    }
    if (nodeKind !== "collection") {
        return {
            key: null,
            issues: [
                { code: "not_collection", message: "filterFnKey is only allowed on collection nodes" },
            ],
        };
    }
    if (typeof value !== "string") {
        return { key: null, issues: [{ code: "not_string", message: "filterFnKey must be a string" }] };
    }
    if (!hasFilterFunctionKey(value)) {
        return {
            key: null,
            issues: [
                {
                    code: "unknown_key",
                    message: `filterFnKey '${value}' is not registered in filter-function-registry`,
                },
            ],
        };
    }
    if (entityTypeKey &&
        !value.startsWith(`${entityTypeKey}.`) &&
        !value.startsWith("projectTreeFolder.")) {
        return {
            key: null,
            issues: [
                {
                    code: "namespace_mismatch",
                    message: `filterFnKey '${value}' must use '${entityTypeKey}.' or 'projectTreeFolder.' namespace`,
                },
            ],
        };
    }
    return { key: value, issues: [] };
}
export function resolveParentValues(fromParent, lineage) {
    if (!fromParent)
        return { parentValues: {}, missingRequired: [] };
    const parentValues = {};
    const missingRequired = [];
    const match = [...lineage]
        .reverse()
        .find((entity) => entity.entityTypeKey === fromParent.entityTypeKey);
    if (!match) {
        missingRequired.push(fromParent.alias);
    }
    else {
        parentValues[fromParent.alias] = {
            entityTypeKey: match.entityTypeKey,
            entityId: match.entityId,
            entitySlug: match.entitySlug,
        };
    }
    return { parentValues, missingRequired };
}
